import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react'
import {
  AlertCircle,
  Clock,
  Loader2,
  Pause,
  Play,
  Plus,
  Square,
  Timer,
  Trash2,
} from 'lucide-react'
import { api, ApiError } from '../../lib/api'
import Modal from '../ui/Modal'
import { formatCents, parseEurosToCents } from '../../lib/money'
import {
  TimeEntryStatusLabel,
  type CreateTimeEntryInput,
  type RateDto,
  type RateListResponse,
  type TimeEntryDto,
  type TimeEntryListResponse,
  type TimeEntryStatusT,
} from '@toga/shared'

interface Props {
  caseId: string
  canWrite: boolean
}

export default function CaseTimePanel({ caseId, canWrite }: Props) {
  const [items, setItems] = useState<TimeEntryDto[]>([])
  const [totalMinutes, setTotalMinutes] = useState(0)
  const [totalAmountCents, setTotalAmountCents] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showForm, setShowForm] = useState<{
    initial: TimeEntryDto | null
    initialMinutes?: number
    initialStart?: string
    initialEnd?: string
  } | null>(null)

  // Cronómetro
  const [timerRunning, setTimerRunning] = useState(false)
  const [timerStart, setTimerStart] = useState<Date | null>(null)
  const [timerElapsed, setTimerElapsed] = useState(0) // segundos acumulados (incluye sesión actual)
  const tickRef = useRef<number | null>(null)

  const reload = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const resp = await api.get<TimeEntryListResponse>(`/time-entries?caseId=${caseId}`)
      setItems(resp.items)
      setTotalMinutes(resp.totalMinutes)
      setTotalAmountCents(resp.totalAmountCents)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error')
    } finally {
      setLoading(false)
    }
  }, [caseId])

  useEffect(() => {
    void reload()
  }, [reload])

  // Tick del cronómetro (cada segundo si está corriendo).
  useEffect(() => {
    if (!timerRunning || !timerStart) return
    tickRef.current = window.setInterval(() => {
      setTimerElapsed(Math.floor((Date.now() - timerStart.getTime()) / 1000))
    }, 1000)
    return () => {
      if (tickRef.current) window.clearInterval(tickRef.current)
    }
  }, [timerRunning, timerStart])

  const startTimer = () => {
    const now = new Date()
    setTimerStart(now)
    setTimerElapsed(0)
    setTimerRunning(true)
  }
  const pauseTimer = () => {
    setTimerRunning(false)
    if (tickRef.current) window.clearInterval(tickRef.current)
  }
  const stopTimer = () => {
    if (!timerStart) return
    const elapsedSec = Math.floor((Date.now() - timerStart.getTime()) / 1000)
    const minutes = Math.max(1, Math.round(elapsedSec / 60))
    pauseTimer()
    setShowForm({
      initial: null,
      initialMinutes: minutes,
      initialStart: timerStart.toISOString(),
      initialEnd: new Date().toISOString(),
    })
    setTimerStart(null)
    setTimerElapsed(0)
  }
  const cancelTimer = () => {
    pauseTimer()
    setTimerStart(null)
    setTimerElapsed(0)
  }

  const onDelete = async (e: TimeEntryDto) => {
    if (!confirm('¿Eliminar este registro?')) return
    try {
      await api.delete(`/time-entries/${e.id}`)
      await reload()
    } catch (err) {
      const body = err instanceof ApiError ? (err.body as { error?: string }) : undefined
      alert(body?.error ?? 'Error')
    }
  }

  return (
    <div className="surface p-5">
      <div className="flex items-center justify-between mb-3">
        <h3 className="t-primary font-semibold flex items-center gap-2">
          <Timer size={14} className="text-toga-300" />
          Tiempos
          {items.length > 0 && (
            <span className="text-xs t-faint font-normal">
              ({formatDuration(totalMinutes)} · {formatCents(totalAmountCents)})
            </span>
          )}
        </h3>
        {canWrite && (
          <div className="flex items-center gap-1.5">
            {!timerRunning && timerStart === null && (
              <button
                type="button"
                onClick={startTimer}
                className="px-2.5 py-1 rounded-lg text-xs font-medium text-emerald-200 bg-emerald-500/15 hover:bg-emerald-500/25 flex items-center gap-1"
                title="Iniciar cronómetro"
              >
                <Play size={11} /> Empezar
              </button>
            )}
            {timerStart !== null && (
              <>
                <span className="font-mono text-sm t-primary">
                  {formatSeconds(timerElapsed)}
                </span>
                {timerRunning ? (
                  <button
                    type="button"
                    onClick={pauseTimer}
                    className="w-7 h-7 grid place-items-center rounded hover:bg-amber-500/15 t-muted hover:text-amber-300"
                    title="Pausar"
                  >
                    <Pause size={12} />
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => {
                      // reanudar: ajustamos timerStart al backstep
                      setTimerStart(new Date(Date.now() - timerElapsed * 1000))
                      setTimerRunning(true)
                    }}
                    className="w-7 h-7 grid place-items-center rounded hover:bg-emerald-500/15 t-muted hover:text-emerald-300"
                    title="Reanudar"
                  >
                    <Play size={12} />
                  </button>
                )}
                <button
                  type="button"
                  onClick={stopTimer}
                  className="px-2 py-1 rounded text-xs t-secondary border border-base hover:bg-hover flex items-center gap-1"
                  title="Parar y guardar"
                >
                  <Square size={10} /> Parar
                </button>
                <button
                  type="button"
                  onClick={cancelTimer}
                  className="text-xs t-faint hover:text-rose-300"
                  title="Cancelar"
                >
                  ✕
                </button>
              </>
            )}
            {timerStart === null && (
              <button
                type="button"
                onClick={() => setShowForm({ initial: null })}
                className="px-2.5 py-1 rounded-lg text-xs font-medium text-toga-200 bg-toga-500/15 hover:bg-toga-500/25 flex items-center gap-1"
                title="Apuntar tiempo manualmente"
              >
                <Plus size={12} /> Manual
              </button>
            )}
          </div>
        )}
      </div>

      {error && (
        <div className="text-xs text-rose-300 flex items-center gap-1 mb-2">
          <AlertCircle size={11} /> {error}
        </div>
      )}

      {loading ? (
        <div className="t-faint text-xs flex items-center gap-1.5">
          <Loader2 size={11} className="animate-spin" /> Cargando…
        </div>
      ) : items.length === 0 ? (
        <p className="text-xs t-faint">Sin tiempos registrados.</p>
      ) : (
        <ul className="divide-y divide-base/40 text-sm">
          {items.map((e) => (
            <li key={e.id} className="py-2 flex items-start gap-2">
              <div className="flex-1 min-w-0">
                <div className="t-primary">{e.description}</div>
                <div className="flex items-center gap-2 text-[11px] t-muted mt-0.5 flex-wrap">
                  <span>{new Date(e.performedOn).toLocaleDateString('es-ES')}</span>
                  <span className="font-mono">{formatDuration(e.durationMinutes)}</span>
                  <span className="font-mono">{formatCents(e.amountCents)}</span>
                  <span>· {e.userName}</span>
                  <StatusBadge status={e.status} />
                </div>
              </div>
              {canWrite && e.status !== 'BILLED' && (
                <button
                  type="button"
                  onClick={() => void onDelete(e)}
                  className="w-6 h-6 grid place-items-center rounded hover:bg-rose-500/10 t-muted hover:text-rose-400"
                  title="Eliminar"
                >
                  <Trash2 size={11} />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {showForm && (
        <TimeEntryFormModal
          caseId={caseId}
          initial={showForm.initial}
          initialMinutes={showForm.initialMinutes}
          initialStart={showForm.initialStart}
          initialEnd={showForm.initialEnd}
          onClose={() => setShowForm(null)}
          onSaved={async () => {
            setShowForm(null)
            await reload()
          }}
        />
      )}
    </div>
  )
}

function StatusBadge({ status }: { status: TimeEntryStatusT }) {
  const STYLES: Record<TimeEntryStatusT, string> = {
    UNBILLED: 'bg-amber-500/10 text-amber-300 border-amber-500/30',
    BILLED: 'bg-emerald-500/10 text-emerald-300 border-emerald-500/30',
    NON_BILLABLE: 'bg-slate-500/10 text-slate-300 border-slate-500/30',
  }
  return (
    <span className={`inline-flex px-1.5 py-0.5 rounded text-[10px] border ${STYLES[status]}`}>
      {TimeEntryStatusLabel[status]}
    </span>
  )
}

function TimeEntryFormModal({
  caseId,
  initial,
  initialMinutes,
  initialStart,
  initialEnd,
  onClose,
  onSaved,
}: {
  caseId: string
  initial: TimeEntryDto | null
  initialMinutes?: number
  initialStart?: string
  initialEnd?: string
  onClose: () => void
  onSaved: () => void | Promise<void>
}) {
  const [description, setDescription] = useState(initial?.description ?? '')
  const [performedOn, setPerformedOn] = useState(
    (initial?.performedOn ?? new Date().toISOString()).slice(0, 10)
  )
  const [durationText, setDurationText] = useState(
    minutesToHM(initialMinutes ?? initial?.durationMinutes ?? 0)
  )
  const [rateEuros, setRateEuros] = useState('')
  const [nonBillable, setNonBillable] = useState(initial?.status === 'NON_BILLABLE')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // F13: tarifas HORA disponibles, para que el abogado elija una del catálogo
  // en lugar de teclear el importe a mano.
  const [hourRates, setHourRates] = useState<RateDto[]>([])
  const [selectedRateId, setSelectedRateId] = useState('')
  useEffect(() => {
    api
      .get<RateListResponse>('/rates?kind=HORA&active=true')
      .then((r) => setHourRates(r.items))
      .catch(() => setHourRates([]))
  }, [])
  const onPickRate = (id: string) => {
    setSelectedRateId(id)
    const r = hourRates.find((x) => x.id === id)
    if (r) {
      setRateEuros((r.amountCents / 100).toFixed(2).replace('.', ','))
    }
  }

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault()
    const minutes = hmToMinutes(durationText)
    if (minutes <= 0) {
      setError('Indica una duración válida (HH:MM o decimal)')
      return
    }
    if (!description.trim()) {
      setError('Indica una descripción')
      return
    }
    setBusy(true)
    setError(null)
    try {
      if (initial) {
        await api.patch(`/time-entries/${initial.id}`, {
          description: description.trim(),
          performedOn: new Date(performedOn).toISOString(),
          durationMinutes: minutes,
          ...(rateEuros ? { hourlyRateCents: parseEurosToCents(rateEuros) } : {}),
        })
      } else {
        const payload: CreateTimeEntryInput = {
          caseId,
          description: description.trim(),
          performedOn: new Date(performedOn).toISOString(),
          durationMinutes: minutes,
          startedAt: initialStart ?? null,
          endedAt: initialEnd ?? null,
          ...(rateEuros ? { hourlyRateCents: parseEurosToCents(rateEuros) } : {}),
          nonBillable,
        }
        await api.post('/time-entries', payload)
      }
      await onSaved()
    } catch (err) {
      const body = err instanceof ApiError ? (err.body as { error?: string }) : undefined
      setError(body?.error ?? (err instanceof Error ? err.message : 'Error'))
    } finally {
      setBusy(false)
    }
  }

  const input =
    'w-full bg-transparent border border-base rounded px-2 py-1.5 text-sm t-primary focus:outline-none focus:border-toga-500/50'

  return (
    <Modal open onClose={onClose} title={initial ? 'Editar tiempo' : 'Nuevo registro de tiempo'}>
      <form onSubmit={onSubmit} className="space-y-3">
        <label className="block">
          <span className="text-xs t-muted">Descripción *</span>
          <input
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Estudio del expediente, preparación de demanda..."
            className={input}
            required
            autoFocus
          />
        </label>
        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="text-xs t-muted">Fecha</span>
            <input
              type="date"
              value={performedOn}
              onChange={(e) => setPerformedOn(e.target.value)}
              className={input}
              required
            />
          </label>
          <label className="block">
            <span className="text-xs t-muted">
              Duración (HH:MM o 1.5 h)
            </span>
            <input
              value={durationText}
              onChange={(e) => setDurationText(e.target.value)}
              placeholder="1:30"
              className={`${input} font-mono`}
              required
            />
          </label>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="text-xs t-muted">Tarifa del catálogo (opcional)</span>
            <select
              value={selectedRateId}
              onChange={(e) => onPickRate(e.target.value)}
              className={input}
              disabled={hourRates.length === 0}
            >
              <option value="">— Usar la mía por defecto —</option>
              {hourRates.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.name} — {(r.amountCents / 100).toFixed(2).replace('.', ',')} €/h
                </option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="text-xs t-muted">€/h aplicado</span>
            <input
              value={rateEuros}
              onChange={(e) => {
                setRateEuros(e.target.value)
                setSelectedRateId('')
              }}
              placeholder="120,00"
              className={`${input} font-mono`}
            />
          </label>
        </div>
        {!initial && (
          <label className="flex items-center gap-2 text-sm t-secondary">
            <input
              type="checkbox"
              checked={nonBillable}
              onChange={(e) => setNonBillable(e.target.checked)}
              className="accent-toga-500"
            />
            No facturable (gestión interna)
          </label>
        )}

        {error && <p className="text-xs text-rose-300">{error}</p>}

        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 rounded-lg text-sm t-secondary border border-base hover:bg-hover"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={busy}
            className="px-3 py-1.5 rounded-lg text-sm font-semibold text-white bg-gradient-to-r from-toga-600 via-toga-500 to-toga-700 disabled:opacity-50 flex items-center gap-1.5"
          >
            {busy && <Loader2 size={14} className="animate-spin" />}
            {initial ? 'Guardar' : 'Registrar'}
          </button>
        </div>
      </form>
    </Modal>
  )
}

function formatDuration(minutes: number): string {
  const h = Math.floor(minutes / 60)
  const m = minutes % 60
  return `${h}h ${String(m).padStart(2, '0')}m`
}

function formatSeconds(secs: number): string {
  const h = Math.floor(secs / 3600)
  const m = Math.floor((secs % 3600) / 60)
  const s = secs % 60
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
}

function minutesToHM(minutes: number): string {
  if (!minutes) return ''
  const h = Math.floor(minutes / 60)
  const m = minutes % 60
  return `${h}:${String(m).padStart(2, '0')}`
}

function hmToMinutes(text: string): number {
  if (!text.trim()) return 0
  const t = text.trim().toLowerCase().replace(/\s*h$/, '').replace(',', '.')
  if (t.includes(':')) {
    const [h, m] = t.split(':')
    return (parseInt(h ?? '0', 10) || 0) * 60 + (parseInt(m ?? '0', 10) || 0)
  }
  const decimal = parseFloat(t)
  if (!Number.isFinite(decimal) || decimal <= 0) return 0
  return Math.round(decimal * 60)
}

void Clock // suprime warning
